import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Server, HardDrive, Activity, RefreshCw, CheckCircle, AlertCircle, X, Trash2, Wifi, WifiOff, Loader2, Edit2 } from 'lucide-react';
import { useAppContext, ServerConnection, ServerType } from '../../context/AppContext';
const panelTypes: {
  value: ServerType;
  label: string;
  description: string;
  defaultPort: number;
  color: string;
}[] = [{
  value: 'cpanel',
  label: 'cPanel / WHM',
  description: 'Industry-standard web hosting control panel',
  defaultPort: 2087,
  color: 'bg-orange-500/10 text-orange-500'
}, {
  value: 'plesk',
  label: 'Plesk',
  description: 'Web hosting platform with WordPress toolkit',
  defaultPort: 8443,
  color: 'bg-blue-500/10 text-blue-500'
}, {
  value: 'sonicpanel',
  label: 'SonicPanel',
  description: 'Streaming media & radio server management',
  defaultPort: 2086,
  color: 'bg-cyan-500/10 text-cyan-500'
}, {
  value: 'directadmin',
  label: 'DirectAdmin',
  description: 'Lightweight, fast web hosting panel',
  defaultPort: 2222,
  color: 'bg-indigo-500/10 text-indigo-500'
}, {
  value: 'cyberpanel',
  label: 'CyberPanel',
  description: 'OpenLiteSpeed-based hosting panel',
  defaultPort: 8090,
  color: 'bg-green-500/10 text-green-500'
}, {
  value: 'cloudpanel',
  label: 'CloudPanel',
  description: 'Free server management for PHP apps',
  defaultPort: 8443,
  color: 'bg-sky-500/10 text-sky-500'
}, {
  value: 'virtualmin',
  label: 'Virtualmin',
  description: 'Powerful open-source hosting control panel',
  defaultPort: 10000,
  color: 'bg-violet-500/10 text-violet-500'
}, {
  value: 'ispconfig',
  label: 'ISPConfig',
  description: 'Open-source multi-server management',
  defaultPort: 8080,
  color: 'bg-amber-500/10 text-amber-500'
}, {
  value: 'centova',
  label: 'Centova Cast',
  description: 'Shoutcast & Icecast stream management',
  defaultPort: 2199,
  color: 'bg-pink-500/10 text-pink-500'
}, {
  value: 'whmcs',
  label: 'WHMCS',
  description: 'Billing & automation for hosting businesses',
  defaultPort: 443,
  color: 'bg-emerald-500/10 text-emerald-500'
}];
const getPanelInfo = (type: ServerType) => panelTypes.find(p => p.value === type) || panelTypes[0];
export function ServerIntegrations() {
  const {
    serverConnections,
    addServerConnection,
    updateServerConnection,
    deleteServerConnection
  } = useAppContext();
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ServerConnection | null>(null);
  const [selectedType, setSelectedType] = useState<ServerType>('cpanel');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const handleSync = (id: string) => {
    setIsSyncing(id);
    setTimeout(() => setIsSyncing(null), 1500);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to remove this server connection?')) {
      deleteServerConnection(id);
    }
  };
  const openAdd = () => {
    setEditingServer(null);
    setSelectedType('cpanel');
    setTestResult(null);
    setIsModalOpen(true);
  };
  const openEdit = (server: ServerConnection) => {
    setEditingServer(server);
    setSelectedType(server.type);
    setTestResult(null);
    setIsModalOpen(true);
  };
  const handleTestConnection = () => {
    setIsTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setIsTesting(false);
      setTestResult(Math.random() > 0.2 ? 'success' : 'error');
    }, 2000);
  };
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data: ServerConnection = {
      id: editingServer ? editingServer.id : `srv_${Date.now()}`,
      name: form.get('name') as string,
      type: selectedType,
      hostname: form.get('hostname') as string,
      port: parseInt(form.get('port') as string),
      apiKey: form.get('apiKey') as string,
      username: form.get('username') as string,
      status: testResult === 'success' ? 'connected' : editingServer ? editingServer.status : 'disconnected',
      lastSync: editingServer ? editingServer.lastSync : new Date().toISOString(),
      notes: form.get('notes') as string,
      stats: editingServer?.stats
    };
    if (editingServer) {
      updateServerConnection(data.id, data);
    } else {
      addServerConnection(data);
    }
    setIsModalOpen(false);
    setEditingServer(null);
  };
  return <div className="space-y-8" data-id="element-658">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-659">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-660">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-661">
            Server Integrations
          </h1>
          <p className="text-slate-400" data-id="element-662">
            Connect and manage servers across all major control panels.
          </p>
        </motion.div>
        <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-663">
          <Plus size={18} data-id="element-664" /> Add Server
        </button>
      </div>

      {/* Supported Panels Info */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5" data-id="element-665">
        <h3 className="text-sm font-bold text-white mb-3" data-id="element-666">
          Supported Control Panels
        </h3>
        <div className="flex flex-wrap gap-2" data-id="element-667">
          {panelTypes.map(p => <span key={p.value} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-800 ${p.color}`} data-id="element-668">
              <Server size={12} data-id="element-669" />
              {p.label}
            </span>)}
        </div>
      </div>

      {/* Server Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-670">
        {serverConnections.map((server, i) => {
        const panel = getPanelInfo(server.type);
        return <motion.div key={server.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-671">
              <div className="flex justify-between items-start mb-6" data-id="element-672">
                <div className="flex items-center gap-3" data-id="element-673">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${panel.color}`} data-id="element-674">
                    <Server size={24} data-id="element-675" />
                  </div>
                  <div data-id="element-676">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-677">
                      {server.name}
                      {server.status === 'connected' ? <CheckCircle size={14} className="text-emerald-500" data-id="element-678" /> : server.status === 'error' ? <AlertCircle size={14} className="text-red-500" data-id="element-679" /> : <WifiOff size={14} className="text-slate-500" data-id="element-680" />}
                    </h3>
                    <p className="text-slate-400 text-sm font-mono" data-id="element-681">
                      {server.hostname}:{server.port}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-682">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border border-slate-800 ${panel.color}`} data-id="element-683">
                    {panel.label}
                  </span>
                </div>
              </div>

              {server.notes && <p className="text-slate-500 text-sm mb-4 italic" data-id="element-684">
                  "{server.notes}"
                </p>}

              {server.stats && <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-900 rounded-xl border border-slate-800" data-id="element-685">
                  <div data-id="element-686">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-687">
                      <Activity size={12} data-id="element-688" /> Accounts
                    </p>
                    <p className="text-white font-bold" data-id="element-689">
                      {server.stats.accounts}
                    </p>
                  </div>
                  <div data-id="element-690">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-691">
                      <HardDrive size={12} data-id="element-692" /> Disk
                    </p>
                    <p className="text-white font-bold" data-id="element-693">
                      {server.stats.diskUsage}
                    </p>
                  </div>
                  <div data-id="element-694">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-695">
                      <Activity size={12} data-id="element-696" /> Bandwidth
                    </p>
                    <p className="text-white font-bold" data-id="element-697">
                      {server.stats.bandwidth}
                    </p>
                  </div>
                </div>}

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-698">
                <p className="text-xs text-slate-500" data-id="element-699">
                  Last sync: {new Date(server.lastSync).toLocaleString()}
                </p>
                <div className="flex items-center gap-3" data-id="element-700">
                  <button onClick={() => openEdit(server)} className="text-sm text-slate-400 hover:text-white transition-colors" data-id="element-701">
                    <Edit2 size={14} data-id="element-702" />
                  </button>
                  <button onClick={() => handleDelete(server.id)} className="text-sm text-slate-400 hover:text-red-400 transition-colors" data-id="element-703">
                    <Trash2 size={14} data-id="element-704" />
                  </button>
                  <button onClick={() => handleSync(server.id)} disabled={isSyncing === server.id} className="flex items-center gap-2 text-sm text-fox-500 hover:text-fox-400 font-medium transition-colors disabled:opacity-50" data-id="element-705">
                    <RefreshCw size={14} className={isSyncing === server.id ? 'animate-spin' : ''} data-id="element-706" />
                    {isSyncing === server.id ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>
            </motion.div>;
      })}

        {serverConnections.length === 0 && <div className="lg:col-span-2 text-center py-16 bg-slate-950 border border-slate-800 rounded-2xl" data-id="element-707">
            <Server size={48} className="mx-auto text-slate-700 mb-4" data-id="element-708" />
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-709">
              No servers connected
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-710">
              Add your first server to start managing your infrastructure.
            </p>
            <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2 rounded-lg font-medium transition-colors" data-id="element-711">
              Add Server
            </button>
          </div>}
      </div>

      {/* Add/Edit Server Modal */}
      <AnimatePresence data-id="element-712">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-713">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-714">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-715">
                <h2 className="text-xl font-bold text-white" data-id="element-716">
                  {editingServer ? 'Edit Server Connection' : 'Add New Server'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-717">
                  <X size={24} data-id="element-718" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6" data-id="element-719">
                {/* Panel Type Selection */}
                <div className="space-y-3" data-id="element-720">
                  <label className="text-sm font-medium text-slate-300" data-id="element-721">
                    Control Panel Type *
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1" data-id="element-722">
                    {panelTypes.map(panel => <button key={panel.value} type="button" onClick={() => setSelectedType(panel.value)} className={`p-3 rounded-xl border text-left transition-all ${selectedType === panel.value ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-723">
                        <div className="flex items-center gap-2 mb-1" data-id="element-724">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${panel.color}`} data-id="element-725">
                            <Server size={12} data-id="element-726" />
                          </div>
                          <span className="text-white font-medium text-sm" data-id="element-727">
                            {panel.label}
                          </span>
                        </div>
                        <p className="text-slate-500 text-[11px] leading-tight" data-id="element-728">
                          {panel.description}
                        </p>
                      </button>)}
                  </div>
                </div>

                {/* Connection Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-729">
                  <div className="space-y-2 md:col-span-2" data-id="element-730">
                    <label className="text-sm font-medium text-slate-300" data-id="element-731">
                      Server Name *
                    </label>
                    <input name="name" required defaultValue={editingServer?.name} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="e.g. US-East Production Server" data-id="element-732" />
                  </div>

                  <div className="space-y-2" data-id="element-733">
                    <label className="text-sm font-medium text-slate-300" data-id="element-734">
                      Hostname / IP *
                    </label>
                    <input name="hostname" required defaultValue={editingServer?.hostname} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="server.example.com" data-id="element-735" />
                  </div>

                  <div className="space-y-2" data-id="element-736">
                    <label className="text-sm font-medium text-slate-300" data-id="element-737">
                      Port *
                    </label>
                    <input name="port" type="number" required defaultValue={editingServer?.port || getPanelInfo(selectedType).defaultPort} key={selectedType} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-738" />
                  </div>

                  <div className="space-y-2" data-id="element-739">
                    <label className="text-sm font-medium text-slate-300" data-id="element-740">
                      Username
                    </label>
                    <input name="username" defaultValue={editingServer?.username} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="root / admin" data-id="element-741" />
                  </div>

                  <div className="space-y-2" data-id="element-742">
                    <label className="text-sm font-medium text-slate-300" data-id="element-743">
                      API Key / Token *
                    </label>
                    <input name="apiKey" required type="password" defaultValue={editingServer?.apiKey} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="••••••••••••" data-id="element-744" />
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-745">
                    <label className="text-sm font-medium text-slate-300" data-id="element-746">
                      Notes
                    </label>
                    <input name="notes" defaultValue={editingServer?.notes} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="Optional description for this server" data-id="element-747" />
                  </div>
                </div>

                {/* Test Connection */}
                <div className="bg-slate-950 border border-slate-800 rounded-xl p-4" data-id="element-748">
                  <div className="flex items-center justify-between" data-id="element-749">
                    <div data-id="element-750">
                      <h4 className="text-white font-medium text-sm" data-id="element-751">
                        Test Connection
                      </h4>
                      <p className="text-slate-500 text-xs" data-id="element-752">
                        Verify credentials before saving
                      </p>
                    </div>
                    <button type="button" onClick={handleTestConnection} disabled={isTesting} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-753">
                      {isTesting ? <Loader2 size={14} className="animate-spin" data-id="element-754" /> : <Wifi size={14} data-id="element-755" />}
                      {isTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                  {testResult && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${testResult === 'success' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`} data-id="element-756">
                      {testResult === 'success' ? <CheckCircle size={16} data-id="element-757" /> : <AlertCircle size={16} data-id="element-758" />}
                      {testResult === 'success' ? 'Connection successful! Server is reachable.' : 'Connection failed. Please check your credentials and try again.'}
                    </motion.div>}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-759">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-760">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-761">
                    {editingServer ? 'Save Changes' : 'Add Server'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}